import {
  AfterContentChecked,
  Component,
  ContentChild,
  ContentChildren,
  Directive,
  EventEmitter,
  Input,
  Output,
  QueryList,
  TemplateRef
} from '@angular/core';

import { isString } from '../util/util';

import { FishAccordionConfig } from './accordion-config';

let nextId = 0;

/**
 * This directive should be used to wrap accordion panel titles that need to contain HTML markup or other directives.
 */

export class FishPanelTitle {
  constructor(public templateRef: TemplateRef<any>) { }
static decorators: DecoratorInvocation[] = [
{ type: Directive, args: [{ selector: 'ng-template[fishPanelTitle]' }, ] },
];
/** @nocollapse */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: TemplateRef, },
];
}

/**
 * This directive must be used to wrap accordion panel content.
 */

export class FishPanelContent {
  constructor(public templateRef: TemplateRef<any>) { }
static decorators: DecoratorInvocation[] = [
{ type: Directive, args: [{ selector: 'ng-template[fishPanelContent]' }, ] },
];
/** @nocollapse */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: TemplateRef, },
];
}

/**
 * The FishPanel directive represents an individual panel with the title and collapsible
 * content
 */

export class FishPanel {
  /**
   *  A flag determining whether the panel is disabled or not.
   *  When disabled, the panel cannot be toggled.
   */
   disabled = false;

  /**
   *  An optional id for the panel. The id should be unique.
   *  If not provided, it will be auto-generated.
   */
   id = `fish-panel-${nextId++}`;

  /**
   *  The title for the panel.
   */
   title: string;

  /**
   *  Accordion's types of panels to be applied per panel basis.
   *  Bootstrap 4 recognizes the following types: "success", "info", "warning" and "danger".
   */
   type: string;

   contentTpl: FishPanelContent;
   titleTpl: FishPanelTitle;
static decorators: DecoratorInvocation[] = [
{ type: Directive, args: [{ selector: 'fish-panel' }, ] },
];
/** @nocollapse */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'disabled': [{ type: Input },],
'id': [{ type: Input },],
'title': [{ type: Input },],
'type': [{ type: Input },],
'contentTpl': [{ type: ContentChild, args: [FishPanelContent, ] },],
'titleTpl': [{ type: ContentChild, args: [FishPanelTitle, ] },],
};
}

/**
 * The payload of the change event fired right before toggling an accordion panel
 */
export interface FishPanelChangeEvent {
  /**
   * Id of the accordion panel that is toggled
   */
  panelId: string;

  /**
   * Whether the panel will be opened (true) or closed (false)
   */
  nextState: boolean;

  /**
   * Function that will prevent panel toggling if called
   */
  preventDefault: () => void;
}

/**
 * The FishAccordion directive is a collection of panels.
 * It can assure that only one panel can be opened at a time.
 */

export class FishAccordion implements AfterContentChecked {
  /**
   * A map that stores each panel state
   */
  private _states: Map<string, boolean> = new Map<string, boolean>();

  /**
   * A map that stores references to all panels
   */
  private _panelRefs: Map<string, FishPanel> = new Map<string, FishPanel>();

   panels: QueryList<FishPanel>;

  /**
   * An array or comma separated strings of panel identifiers that should be opened
   */
   activeIds: string | string[] = [];

  /**
   *  Whether the other panels should be closed when a panel is opened
   */
   closeOtherPanels: boolean;

  /**
   *  Accordion's types of panels to be applied globally.
   *  Bootstrap 4 recognizes the following types: "success", "info", "warning" and "danger".
   */
   type: string;

  /**
   * A panel change event fired right before the panel toggle happens. See FishPanelChangeEvent for payload details
   */
   panelChange = new EventEmitter<FishPanelChangeEvent>();

  constructor(config: FishAccordionConfig) {
    this.type = config.type;
    this.closeOtherPanels = config.closeOthers;
  }

  /**
   * Programmatically toggle a panel with a given id.
   */
  toggle(panelId: string) {
    const panel = this._panelRefs.get(panelId);

    if (panel && !panel.disabled) {
      const nextState = !this._states.get(panelId);
      let defaultPrevented = false;

      this.panelChange.emit(
        { panelId: panelId, nextState: nextState, preventDefault: () => { defaultPrevented = true; } });

      if (!defaultPrevented) {
        this._states.set(panelId, nextState);

        if (this.closeOtherPanels) {
          this._closeOthers(panelId);
        }
        this._updateActiveIds();
      }
    }
  }

  ngAfterContentChecked() {
    // active id updates
    if (isString(this.activeIds)) {
      this.activeIds = this.activeIds.split(/\s*,\s*/);
    }
    this._updateStates();

    // closeOthers updates
    if (this.activeIds.length > 1 && this.closeOtherPanels) {
      this._closeOthers(this.activeIds[0]);
      this._updateActiveIds();
    }
  }

  /**
   * @internal
   */
  isOpen(panelId: string): boolean { return this._states.get(panelId); }

  private _closeOthers(panelId: string) {
    this._states.forEach((state, id) => {
      if (id !== panelId) {
        this._states.set(id, false);
      }
    });
  }

  private _updateActiveIds() {
    this.activeIds =
      this.panels.toArray().filter(panel => this.isOpen(panel.id) && !panel.disabled).map(panel => panel.id);
  }

  private _updateStates() {
    this._states.clear();
    this._panelRefs.clear();
    this.panels.toArray().forEach((panel) => {
      this._states.set(panel.id, this.activeIds.indexOf(panel.id) > -1 && !panel.disabled);
      this._panelRefs.set(panel.id, panel);
    });
  }
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  selector: 'fish-accordion',
  exportAs: 'fishAccordion',
  host: { 'role': 'tablist', '[attr.aria-multiselectable]': '!closeOtherPanels' },
  template: `
  <div class="card">
    <ng-template ngFor let-panel [ngForOf]="panels">
      <div role="tab" id="{{panel.id}}-header"
        [class]="'card-header ' + (panel.type ? 'card-'+panel.type: type ? 'card-'+type : '')" [class.active]="isOpen(panel.id)">
        <a href (click)="!!toggle(panel.id)" [class.text-muted]="panel.disabled" [attr.tabindex]="(panel.disabled ? '-1' : null)"
          [attr.aria-expanded]="isOpen(panel.id)" [attr.aria-controls]="(isOpen(panel.id) ? panel.id : null)"
          [attr.aria-disabled]="panel.disabled">
          {{panel.title}}<ng-template [ngTemplateOutlet]="panel.titleTpl?.templateRef"></ng-template>
        </a>
      </div>
      <div id="{{panel.id}}" role="tabpanel" [attr.aria-labelledby]="panel.id + '-header'" class="card-block" *ngIf="isOpen(panel.id)">
        <ng-template [ngTemplateOutlet]="panel.contentTpl.templateRef"></ng-template>
      </div>
    </ng-template>
  </div>
`
}, ] },
];
/** @nocollapse */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: FishAccordionConfig, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'panels': [{ type: ContentChildren, args: [FishPanel, ] },],
'activeIds': [{ type: Input },],
'closeOtherPanels': [{ type: Input, args: ['closeOthers', ] },],
'type': [{ type: Input },],
'panelChange': [{ type: Output },],
};
}

interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
